import React, { useEffect, useRef, useState } from 'react';
import { getHeaderUrl, getHeaderFooterOrigin, HEADER_FOOTER_STYLESHEET_PATH, rewriteHeaderFooterAssetUrl } from '../services/configService';

interface TopNavProps {
  onMenu: () => void;
  customStyles?: string;
}

/**
 * TopNav - The global navigation bar with logo and hamburger menu.
 *
 * Fetches header HTML from a remote URL and displays it with complete style isolation
 * using Shadow DOM. This ensures that the app's styling cannot impact the header's
 * HTML/CSS and vice versa.
 *
 * The component uses Shadow DOM to create a completely isolated styling context,
 * preventing any CSS leakage between the app and the header content.
 *
 * You can provide custom styles via the `customStyles` prop, which will be injected
 * into the Shadow DOM and only affect the header content, maintaining full isolation.
 *
 * The `onMenu` callback is preserved and will be attached to menu buttons in the
 * fetched header content.
 *
 * @param onMenu - Handler function to open the SideMenu when the hamburger button is clicked
 * @param customStyles - Optional CSS string to inject into the header's Shadow DOM.
 *                       These styles will only affect the header and won't leak to the app.
 *
 * @example
 * ```tsx
 * <TopNav onMenu={() => setMenuOpen(true)} />
 * ```
 *
 * @example
 * ```tsx
 * <TopNav 
 *   onMenu={() => setMenuOpen(true)}
 *   customStyles={`
 *     header {
 *       background-color: #custom-color !important;
 *       padding: 20px;
 *     }
 *     .navigation a {
 *       color: #fff;
 *     }
 *   `} 
 * />
 * ```
 */

const TopNav: React.FC<TopNavProps> = ({ onMenu, customStyles }) => {
  const remoteStylesheetUrl = `${getHeaderFooterOrigin()}${HEADER_FOOTER_STYLESHEET_PATH}`;
  const stylesheetBaseUrl = `${getHeaderFooterOrigin()}/wp-content/themes/mindful-therapy-group/css/`;
  const containerRef = useRef<HTMLDivElement>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const stylesheetLoadedRef = useRef(false);
  const headerFetchedRef = useRef(false);

  // Function to fetch and inject the remote stylesheet into Shadow DOM
  const injectRemoteStylesheet = React.useCallback(async () => {
    if (!shadowRootRef.current || stylesheetLoadedRef.current) return;

    // Check if stylesheet is already loaded
    const existingStylesheet = shadowRootRef.current.querySelector('style[data-remote-header-stylesheet]');
    if (existingStylesheet) {
      stylesheetLoadedRef.current = true;
      return;
    }

    try {
      // Fetch the CSS content
      const response = await fetch(remoteStylesheetUrl);
      if (!response.ok) {
        console.warn('Failed to fetch remote stylesheet:', response.status);
        return;
      }

      let cssContent = await response.text();

      // Handle @import statements - convert relative URLs to absolute
      cssContent = cssContent.replace(
        /@import\s+["']([^"']+)["']/g,
        (match, url) => {
          if (url.startsWith('http') || url.startsWith('//')) {
            return match; // Already absolute
          }
          // Convert relative URL to absolute
          const baseUrl = stylesheetBaseUrl;
          const absoluteUrl = new URL(url, baseUrl).href;
          return `@import "${absoluteUrl}"`;
        }
      );

      // Create and inject the style tag with CSS content
      const styleTag = document.createElement('style');
      styleTag.setAttribute('data-remote-header-stylesheet', 'true');
      styleTag.textContent = cssContent;
      
      // Insert at the beginning so our custom styles can override if needed
      if (shadowRootRef.current.firstChild) {
        shadowRootRef.current.insertBefore(styleTag, shadowRootRef.current.firstChild);
      } else {
        shadowRootRef.current.appendChild(styleTag);
      }
      
      stylesheetLoadedRef.current = true;
    } catch (err) {
      console.error('Error loading remote stylesheet:', err);
    }
  }, []);

  // Function to inject default header-scoped styles
  const injectDefaultHeaderStyles = React.useCallback(() => {
    if (!shadowRootRef.current) return;

    // Remove any existing default header styles
    const existingDefaultStyle = shadowRootRef.current.querySelector('style[data-default-header-styles]');
    if (existingDefaultStyle) {
      existingDefaultStyle.remove();
    }

    // Inject default header-scoped styles (font-size override)
    const defaultStyleTag = document.createElement('style');
    defaultStyleTag.setAttribute('data-default-header-styles', 'true');
    defaultStyleTag.textContent = `
      header {
        font-size: 14px;
      }
    `;
    shadowRootRef.current.appendChild(defaultStyleTag);
  }, []);

  // Function to inject custom styles into Shadow DOM
  const injectCustomStyles = React.useCallback(() => {
    if (!shadowRootRef.current) return;

    // Remove any existing custom styles
    const existingCustomStyle = shadowRootRef.current.querySelector('style[data-custom-header-styles]');
    if (existingCustomStyle) {
      existingCustomStyle.remove();
    }

    // Inject custom styles if provided (these will only affect the header in Shadow DOM)
    // Custom styles are injected after the remote stylesheet so they can override if needed
    if (customStyles) {
      const customStyleTag = document.createElement('style');
      customStyleTag.setAttribute('data-custom-header-styles', 'true');
      customStyleTag.textContent = customStyles;
      shadowRootRef.current.appendChild(customStyleTag);
    }
  }, [customStyles]);

  // Function to attach menu click handlers to header buttons
  const attachMenuHandlers = React.useCallback(() => {
    if (!shadowRootRef.current) return;

    // Find menu buttons - common selectors for menu/hamburger buttons
    const menuSelectors = [
      '.menu-btn',
      '.menu-button',
      '.hamburger',
      '.nav-toggle',
      '[aria-label="Menu"]',
      'button[aria-label*="menu" i]',
      'button[aria-label*="Menu" i]',
    ];

    menuSelectors.forEach((selector) => {
      const menuButtons = shadowRootRef.current!.querySelectorAll(selector);
      menuButtons.forEach((button) => {
        // Remove existing listeners by cloning
        const newButton = button.cloneNode(true) as HTMLElement;
        button.parentNode?.replaceChild(newButton, button);
        
        // Attach click handler
        newButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          onMenu();
        });
      });
    });
  }, [onMenu]);

  // Fetch header content (only once on mount)
  useEffect(() => {
    // Prevent double-fetching in React StrictMode
    if (headerFetchedRef.current) return;
    headerFetchedRef.current = true;

    const fetchHeader = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(getHeaderUrl());
        
        if (!response.ok) {
          throw new Error(`Failed to fetch header: ${response.status} ${response.statusText}`);
        }

        // The API returns a JSON string, so we need to parse it first
        const jsonText = await response.text();
        let htmlContent: string;
        
        try {
          // Parse the JSON to get the actual HTML string
          htmlContent = JSON.parse(jsonText);
        } catch {
          // If it's not valid JSON, use the text directly
          htmlContent = jsonText;
        }

        // After JSON parsing, replace any remaining literal escape sequences
        // (in case of double-encoding or if JSON.parse didn't handle them)
        htmlContent = htmlContent
          .replace(/\\n/g, '\n')
          .replace(/\\t/g, '\t');

        // Initialize Shadow DOM if not already created
        if (containerRef.current && !shadowRootRef.current) {
          shadowRootRef.current = containerRef.current.attachShadow({ mode: 'open' });
        }

        if (shadowRootRef.current) {
          // Clear previous content
          shadowRootRef.current.innerHTML = '';
          stylesheetLoadedRef.current = false; // Reset flag

          // FIRST: Load the remote stylesheet BEFORE inserting header HTML
          // This ensures styles are ready when the HTML is added
          await injectRemoteStylesheet();

          // Create a temporary container to parse the HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = htmlContent;

          // Extract and inject any <style> tags from the HTML into the shadow DOM
          const styleTags = tempDiv.querySelectorAll('style');
          styleTags.forEach((styleTag) => {
            const newStyle = document.createElement('style');
            newStyle.textContent = styleTag.textContent;
            shadowRootRef.current!.appendChild(newStyle);
            styleTag.remove();
          });

          // Extract and inject any <link> tags for stylesheets from the HTML
          const linkTags = tempDiv.querySelectorAll('link[rel="stylesheet"]');
          linkTags.forEach((linkTag) => {
            const newLink = document.createElement('link');
            newLink.rel = 'stylesheet';
            const href = linkTag.getAttribute('href') || '';
            newLink.href = rewriteHeaderFooterAssetUrl(href);
            shadowRootRef.current!.appendChild(newLink);
            linkTag.remove();
          });

          // Remove script tags and other non-header content
          tempDiv.querySelectorAll('script').forEach((script) => script.remove());
          
          // Check if header already exists in shadow DOM (prevent duplicate rendering)
          const existingHeader = shadowRootRef.current.querySelector('header');
          if (!existingHeader) {
            // Extract only the header element
            const headerElement = tempDiv.querySelector('header');
            
            if (headerElement) {
              // Clone and append the header content to shadow DOM
              // Styles are already loaded, so they should apply immediately
              const headerClone = headerElement.cloneNode(true);
              shadowRootRef.current.appendChild(headerClone);
            } else {
              // Fallback: if no header element found, use the entire content
              const contentClone = tempDiv.cloneNode(true);
              shadowRootRef.current.appendChild(contentClone);
            }
          }

          // Inject default header-scoped styles (font-size override)
          injectDefaultHeaderStyles();

          // Inject custom styles AFTER header HTML and remote stylesheet are loaded
          injectCustomStyles();

          // Attach menu handlers after content is loaded
          attachMenuHandlers();
        }
      } catch (err) {
        console.error('Error fetching header:', err);
        setError(err instanceof Error ? err.message : 'Failed to load header');
      } finally {
        setIsLoading(false);
      }
    };

    fetchHeader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle custom styles updates (re-inject when customStyles prop changes)
  useEffect(() => {
    if (!shadowRootRef.current) return;
    injectCustomStyles();
  }, [injectCustomStyles]);

  // Re-attach menu handlers when onMenu changes
  useEffect(() => {
    if (!shadowRootRef.current) return;
    attachMenuHandlers();
  }, [attachMenuHandlers]);

  return (
    <div 
      ref={containerRef}
      style={{
        // Minimal container styling - won't affect shadow DOM content
        width: '100%',
        display: 'block'
      }}
    >
      {isLoading && (
        <div style={{ padding: '12px', textAlign: 'center' }}>
          <div className="loading-spinner" style={{ width: '24px', height: '24px', borderWidth: '3px', margin: '0 auto' }} />
        </div>
      )}
      {error && (
        <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
          Error loading header: {error}
        </div>
      )}
    </div>
  );
};

export default TopNav; 